/**
 * shareApi — KY Report 임시 토큰 공유 발행·관리 (스태프 인증, v2 envelope).
 * 계약: STUDENT_REPORT_SHARE_BE_REPLY.md §5 (dev@0373e2a e2e 검증 기준).
 *   - 발행:     POST   /api/v2/report-share            {report_id, max_views?=100, expires_days?=7}
 *   - 목록:     GET    /api/v2/report-share?student_id=&report_id=&page=&per_page=
 *   - 강제만료: DELETE /api/v2/report-share/{share_id}
 * 공개 열람(무인증)은 student 앱 쪽(student/lib/shareApi.js)이 담당 — 여기서 다루지 않음.
 * 주의: share_token·url은 발행 응답에서만 옴(목록엔 미노출) — 발행 직후 화면에서 복사시켜야 함.
 */
import { fetchWithTokenRefresh } from '../../utils/apiUtils';
import { parseV2Timestamp, v2KstDayMs } from '../../utils/v2DateTime';

export const SHARE_DEFAULT_MAX_VIEWS = 100;
export const SHARE_DEFAULT_EXPIRES_DAYS = 7;

// 발행 → { share_id, share_token(43자), url(학생 페이지 완성 URL), report_id, student_id,
//          max_views, views_used, views_remaining, expires_at, revoked, created_by, created_at }
export async function createReportShare(reportId, getAccessToken, options = {}) {
  const { maxViews = SHARE_DEFAULT_MAX_VIEWS, expiresDays = SHARE_DEFAULT_EXPIRES_DAYS } = options;
  const res = await fetchWithTokenRefresh('/api/v2/report-share', {
    method: 'POST',
    body: JSON.stringify({ report_id: reportId, max_views: maxViews, expires_days: expiresDays }),
  }, getAccessToken);
  const data = res?.data ?? null;
  if (!data || !data.share_token || !data.url) {
    throw new Error('공유 링크 발행 응답이 올바르지 않습니다(share_token/url 없음).');
  }
  return data;
}

// 목록 → items[{ share_id, report_id, student_name(내부용 원문), max_views, views_used,
//               expires_at, revoked, created_by, created_at }] — 토큰·url 미포함
export async function fetchReportShares(filters, getAccessToken) {
  const { studentId, reportId, page, perPage } = filters || {};
  const qs = new URLSearchParams();
  if (studentId != null) qs.set('student_id', studentId);
  if (reportId != null) qs.set('report_id', reportId);
  if (page != null) qs.set('page', page);
  if (perPage != null) qs.set('per_page', perPage);
  const query = qs.toString();
  const res = await fetchWithTokenRefresh(`/api/v2/report-share${query ? `?${query}` : ''}`, {}, getAccessToken);
  const d = res?.data;
  // reportApi.fetchReportList과 동일한 방어적 정규화(중첩 별칭 수용)
  const items = Array.isArray(d) ? d
    : Array.isArray(d?.items) ? d.items
      : Array.isArray(d?.list) ? d.list
        : [];
  return { items, total: d?.total ?? items.length, page: d?.page ?? 1 };
}

// 학생의 공유 전체 수집 — 목록이 페이지네이션이라 total까지 순회(이력 누적 시 유효 공유 누락 방지).
// 백엔드가 page 파라미터를 무시하고 전량을 주면 1회에 끝난다(total 기본값 = items.length).
export async function fetchAllReportShares(studentId, getAccessToken, { perPage = 100, maxPages = 5 } = {}) {
  const all = [];
  for (let page = 1; page <= maxPages; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const { items, total } = await fetchReportShares({ studentId, page, perPage }, getAccessToken);
    all.push(...items);
    if (!items.length || all.length >= total) break;
  }
  return { items: all };
}

// 강제 만료 → { share_id, revoked: true }
export async function revokeReportShare(shareId, getAccessToken) {
  const res = await fetchWithTokenRefresh(`/api/v2/report-share/${shareId}`, { method: 'DELETE' }, getAccessToken);
  return res?.data ?? { share_id: shareId, revoked: true };
}

// ── 상태 계산 (순수 — 목록 아이템·발행 응답 공용) ──

export function shareViewsRemaining(share) {
  if (!share) return 0;
  if (share.views_remaining != null) return share.views_remaining; // 발행 응답
  if (share.max_views != null && share.views_used != null) return Math.max(0, share.max_views - share.views_used);
  return 0;
}

export function isShareActive(share, now = Date.now()) {
  if (!share || share.revoked) return false;
  const exp = parseV2Timestamp(share.expires_at)?.getTime();
  if (exp == null || exp <= now) return false;
  return shareViewsRemaining(share) > 0;
}

// 잔여기간 라벨: 달력일 기준 D-n / '오늘 만료'(만료일이 오늘).
// 경과시간(24h 창) 기준이면 기본 7일 발행 직후 'D-6', 자정 걸친 내일 만료가 '오늘 만료'로 어긋난다.
export function shareDdayLabel(share, now = Date.now()) {
  const exp = v2KstDayMs(share?.expires_at);
  const today = v2KstDayMs(now);
  if (exp == null || today == null) return '';
  const days = Math.round((exp - today) / 86400000);
  return days >= 1 ? `D-${days}` : '오늘 만료';
}

// 목록 → report_id별 유효 공유(최신 1건). 만료·취소분은 제외.
export function buildActiveShareMap(items, now = Date.now()) {
  const map = new Map();
  (items || []).forEach((s) => {
    if (!isShareActive(s, now)) return;
    const prev = map.get(s.report_id);
    if (!prev || (parseV2Timestamp(s.created_at)?.getTime() ?? 0) > (parseV2Timestamp(prev.created_at)?.getTime() ?? 0)) {
      map.set(s.report_id, s);
    }
  });
  return map;
}
